<script setup lang="tsx">
import { computed, reactive, ref } from 'vue'
import SelectorModal from '@/views/chat/components/selector-modal.vue'
import SingleSelector from '@/views/chat/components/single-selector.vue'
import ProductSelector from '@/views/chat/components/product-selector.vue'
import { dictCache, dictLabel } from '@/utils/util'
import {
  getDistributorNamesApi,
  getProductsApi,
  getQuestionsApi,
  getTimeOptionsApi,
} from '@/api/distributorApi'
import DatePicker from '@/components/date-picker.vue'
import db from '@/utils/db'
import moment from 'moment'
import Schema from 'async-validator'
import { showToast } from '@nutui/nutui'
import type { DictItem, IApiOptions } from '@/types'

type FormType = Omit<IApiOptions['Distributor'], 'end_date'> & {
  end_date?: Date
}
const emit = defineEmits(['ok'])
const visible = ref(false)
const form = reactive<FormType>({
  date_span_option: '',
  distributor: '',
  target_questions: [],
  products: [],
  end_date: moment().date(1).toDate(),
})
const rules = {
  distributor: [{ required: true, message: '请选择经销商' }],
  target_questions: [{ required: true, message: '请选择问题' }],
  date_span_option: [{ required: true, message: '请选择时间类型' }],
}

const products = computed(() => {
  return form.products.map((item) => {
    return productsCache.value[item]
  })
})
const questions = computed(() => {
  return form.target_questions.map((item) => questionCache.value[item]).join(',')
})

const dict = reactive<Record<string, DictItem[]>>({
  distributor: [],
  questions: [],
  products: [],
  date_span_option: [],
})

const questionCache = computed(() => dictCache(dict.questions, 3))
const productsCache = computed(() => dictCache(dict.products, 3))

type ModalKey = 'dealer' | 'question' | 'products'
const modalRef = reactive<{
  [p in ModalKey]: InstanceType<typeof SelectorModal> | null
}>({
  dealer: null,
  question: null,
  products: null,
})

function bindModal(prop: ModalKey) {
  return (modal: unknown) => {
    modalRef[prop] = modal as InstanceType<typeof SelectorModal>
  }
}

function SelectInput(props: { value?: string } = { value: '' }): JSX.Element {
  return (
    <div class="select-input">
      <span class="input-content">{props.value}</span>
      <v-icon icon="mdi-chevron-right" />
    </div>
  )
}

async function validate(data: IApiOptions['Distributor']) {
  const dynamicRules = { ...rules }
  if (data.date_span_option === 'YTD' || data.date_span_option === '月') {
    Reflect.set(dynamicRules, 'end_date', [{ required: true, message: '请选择截至月份' }])
  }
  const validator = new Schema(dynamicRules)
  return validator.validate(data).catch((err) => {
    showToast.text(err.errors[0].message)
    return Promise.reject(err)
  })
}

const loading = ref(false)
function loadData() {
  loading.value = true
  return Promise.all([
    getProductsApi().then((res) => (dict.products = res)),
    getQuestionsApi().then((res) => (dict.questions = res)),
    getDistributorNamesApi().then((res) => {
      dict.distributor = res.distributorNames.map((item: string) => ({
        label: item,
        value: item,
      }))
    }),
    getTimeOptionsApi().then((res) => {
      dict.date_span_option = res.timeOptions.map((label: string) => ({
        label,
        value: label,
      }))
      if (!form.date_span_option) {
        form.date_span_option = dict.date_span_option[0].value
      }
    }),
  ]).finally(() => (loading.value = false))
}

async function handleOk() {
  const data = JSON.parse(JSON.stringify(form))
  if (['YTD', '月'].includes(data.date_span_option)) {
    if (data.end_date) {
      data.end_date = moment(data.end_date).format('YYYY-MM-DD')
    }
  } else {
    Reflect.deleteProperty(data, 'end_date')
  }
  await validate(data)
  db.setJson('archive_search', data)
  emit('ok', data)
  visible.value = false
}

defineExpose({
  open() {
    const data = db.getJson('archive_search')
    if (data) {
      form.distributor = data.distributor
      form.target_questions = data.target_questions
      form.products = data.products
      form.date_span_option = data.date_span_option
      const m = data.end_date ? moment(data.end_date) : moment().date(1)
      form.end_date = m.toDate()
    }
    loadData()
    visible.value = true
  },
})
const persistent = ref(false)
const date = computed({
  get: () => {
    return form.end_date ? moment(form.end_date).toDate() : new Date()
  },
  set: (data) => {
    form.end_date = data
  },
})

function handleRemoveProductItem(index: number) {
  form.products.splice(index, 1)
}
// <!--scroll-strategy 'close' | 'block' | 'none' | 'reposition'   -->
</script>

<template>
  <v-overlay no-click-animation class="archive-modal" v-model="visible" :persistent="persistent" scroll-strategy="reposition">
    <div class="model-wrapper" v-loading="loading">
      <div class="header">
        <a @click="visible = false">取消</a>
        <span>经销商档案筛选</span>
        <a class="modal-ok-btn" @click="handleOk">确认</a>
      </div>

      <div class="content">
        <div class="content-wrapper abs-full">
          <div class="form-item">
            <div class="form-label form-required">选择经销商</div>
            <div class="form-control">
              <select-input
                @click="modalRef.dealer?.open"
                :value="dictLabel(form.distributor, dict.distributor)"
              />
            </div>
          </div>
          <div class="form-item">
            <div class="form-label form-required">选择问题</div>
            <div class="form-control">
              <select-input @click="modalRef.question?.open" :value="questions" />
            </div>
          </div>
          <div class="form-item">
            <div class="form-label form-required">选择时间</div>
            <div class="form-control">
              <div class="select-time">
                <template v-for="item in dict.date_span_option">
                  <div
                    class="time-item text-center"
                    :class="{ active: form.date_span_option === item.value }"
                    @click="form.date_span_option = item.value"
                  >
                    {{ item.label }}
                  </div>
                </template>
              </div>
              <template v-if="form.date_span_option === 'YTD' || form.date_span_option === '月'">
                <date-picker v-model="date" v-model:subpopup="persistent" value-format="YYYY-MM">
                  <template v-slot="{ format }">
                    <div class="d-flex align-center ymd-row">
                      <div class="ymd-label">截止月份</div>
                      <div style="flex: 1">{{ format }}</div>
                      <v-icon icon="mdi-calendar-month-outline" />
                    </div>
                  </template>
                </date-picker>
              </template>
            </div>
          </div>

          <div class="form-item">
            <div class="form-label">
              选择产品<span>（多选）</span
              ><span class="product-tips">若不选择该项，则默认全选</span>
            </div>
            <div class="form-control">
              <select-input @click="modalRef.products?.open" />
              <div class="selected-label">已选:</div>
              <div class="selected-products">
                <template v-for="(item, index) in products">
                  <span class="product">
                    {{ item }}
                    <v-icon icon="mdi-close-circle" @click="handleRemoveProductItem(index)" />
                  </span>
                </template>
              </div>
            </div>
          </div>

          <selector-modal
            :ref="bindModal('dealer')"
            title="选择经销商"
            v-model="form.distributor"
            class="sub-overlay"
          >
            <single-selector :list="dict.distributor" />
          </selector-modal>
          <selector-modal
            :ref="bindModal('question')"
            title="选择问题"
            v-model="form.target_questions"
            class="sub-overlay"
          >
            <product-selector :list="dict.questions" />
          </selector-modal>
          <selector-modal
            :ref="bindModal('products')"
            title="选择产品"
            v-model="form.products"
            class="sub-overlay"
          >
            <product-selector top :list="dict.products" />
          </selector-modal>
        </div>
      </div>
    </div>
  </v-overlay>
</template>

<style lang="scss">
.archive-modal {
  > .v-overlay__content {
    position: absolute;
    bottom: 0;
    top: 2rem;
    width: 100%;
  }
  .product-tips {
    font-weight: lighter;
    font-size: small;
    color: grey;
  }
  .model-wrapper {
    height: 100%;
    background-color: #fff;
    border-radius: 0.747rem 0.747rem 0 0;
    display: flex;
    flex-direction: column;
  }

  .header {
    height: 2.987rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 0.747rem;
    border-bottom: 1px solid #f3f4f4;
    font-size: 0.96rem;
  }

  .content {
    position: relative;
    flex: 1;
  }

  .content-wrapper {
    padding: 0 0.853rem;
    overflow-y: auto;
  }

  .modal-ok-btn {
    color: var(--primary-color);
  }

  .form-item {
    margin-bottom: 0.853rem;
  }

  .form-label {
    line-height: 1;
    padding: 1.04rem 0 0.853rem;
    font-size: 0.85rem;
    font-weight: bold;
    color: #333333;
  }

  .select-input {
    background-color: #f3f3f3;
    border-radius: 2rem;
    height: 1.813rem;
    display: flex;
    align-items: center;
    padding: 0 2.1rem 0 0.8rem;
    color: #333333;
    position: relative;

    .input-content {
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .v-icon {
      position: absolute;
      right: 0.8rem;
      top: 50%;
      transform: translateY(-50%);
    }
  }

  .select-time {
    display: flex;
  }

  .time-item {
    line-height: 1.81rem;
    color: #666666;
    flex: 1;
    background-color: #eff3fc;
    box-sizing: border-box;
    border: 1px solid #eff3fc;
    border-radius: 0.27rem;
    font-size: 0.69rem;

    + .time-item {
      margin-left: 0.507rem;
    }

    &.active {
      border-color: var(--primary-color);
      color: var(--primary-color);
      background-color: #e5f4fc;
    }
  }

  .selected-label {
    padding: 0.8rem 0 0.56rem;
    font-size: 0.69rem;
    font-weight: 500;
    color: #333333;
  }

  .selected-products {
    display: flex;
    flex-wrap: wrap;

    .product {
      display: inline-flex;
      align-items: center;
      color: var(--primary-color);
      background: #0091df19;
      border-radius: 0.27rem;
      padding: 0.267rem 0.507rem;
      margin-right: 0.373rem;
      margin-bottom: 0.427rem;

      .v-icon {
        margin-left: 0.3rem;
        font-size: 0.8rem;
      }
    }
  }

  .sub-overlay {
    background-color: #fff;
  }

  .ymd-row {
    height: 2.6rem;
    font-size: 0.75rem;
    border-bottom: 1px solid #f3f4f4;

    .ymd-label {
      font-weight: 500;
      width: 5rem;
    }

    .v-icon {
      color: #999;
    }
  }
}
</style>
